import React from 'react';
import { Box, Pagination } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';
import { BulkActionsPageSizeSelect } from './AppBulkActions';
import { DEFAULT_PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';

interface AppPaginationProps {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  itemLabel?: string;
  pageSizeOptions?: readonly number[];
  inline?: boolean;
  containerSx?: SxProps<Theme>;
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export default function AppPagination({
  count,
  page,
  pageSize,
  totalPages,
  itemLabel = 'items',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  inline = false,
  containerSx,
  onPageChange,
  onPageSizeChange,
}: AppPaginationProps) {
  const resolvedPageSizeOptions = React.useMemo(() => {
    const filteredOptions = pageSizeOptions.filter((option) => option <= count);
    const nextLargerOption = pageSizeOptions.find((option) => option > count);
    const optionsWithNext = nextLargerOption !== undefined
      ? [...filteredOptions, nextLargerOption]
      : filteredOptions;
    const optionsWithCurrent = optionsWithNext.includes(pageSize)
      ? optionsWithNext
      : [...optionsWithNext, pageSize];

    return Array.from(new Set(optionsWithCurrent)).sort((left, right) => left - right);
  }, [count, pageSize, pageSizeOptions]);

  if (count <= DEFAULT_PAGE_SIZE) {
    return null;
  }

  const resolveSxObject = (sx: SxProps<Theme> | undefined, theme: Theme): Record<string, unknown> => {
    if (!sx) {
      return {};
    }

    if (Array.isArray(sx)) {
      return sx.reduce<Record<string, unknown>>((acc, item) => {
        return { ...acc, ...resolveSxObject(item as SxProps<Theme>, theme) };
      }, {});
    }

    if (typeof sx === 'function') {
      return resolveSxObject(sx(theme), theme);
    }

    if (typeof sx === 'object') {
      return sx as Record<string, unknown>;
    }

    return {};
  };

  const containerStyle: SxProps<Theme> = (theme: Theme) => {
    const baseStyle = inline ? {} : resolveSxObject(sharedListStyles.paginationContainer as SxProps<Theme>, theme);
    return {
      ...baseStyle,
      ...resolveSxObject(containerSx, theme),
    };
  };

  return (
    <Box sx={containerStyle}>
      {!inline && <Box sx={{ flex: 1 }} />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BulkActionsPageSizeSelect
          pageSize={pageSize}
          pageSizeOptions={resolvedPageSizeOptions}
          onPageSizeChange={onPageSizeChange}
        />
        <Pagination
          color="primary"
          shape="rounded"
          page={page}
          count={Math.max(totalPages, 1)}
          onChange={onPageChange}
          showFirstButton
          showLastButton
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    </Box>
  );
}
